import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { debug } from './debug';

const CACHE_DIR = 'cached-images';
const CACHE_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

/**
 * Get the base URL for fetching images
 * In web, use current origin; in native, use production URL
 */
const getBaseUrl = (): string => {
  if (Capacitor.isNativePlatform()) {
    // Use your production URL - update this to match your deployed site
    return 'https://keep.cruisingalong.com'; // ⚠️ UPDATE THIS with your actual domain
  }
  return window.location.origin;
};

/**
 * Convert image URL to cache key (filename)
 */
const getCacheKey = (imageUrl: string): string => {
  // Remove leading slash and replace slashes with underscores
  const key = imageUrl.replace(/^\//, '').replace(/\//g, '_');
  // Ensure it's a valid filename
  return key.replace(/[^a-zA-Z0-9._-]/g, '_');
};

/**
 * Check if image exists in cache and return native URI
 */
export const getCachedImageUri = async (imageUrl: string): Promise<string | null> => {
  if (!Capacitor.isNativePlatform() || !imageUrl) {
    return null; // No caching needed for web
  }

  try {
    const cacheKey = getCacheKey(imageUrl);
    const filePath = `${CACHE_DIR}/${cacheKey}`;
    
    const stat = await Filesystem.stat({
      path: filePath,
      directory: Directory.Cache,
    });

    // Check if cache is still valid (not expired)
    const now = Date.now();
    const fileTime = stat.mtime ? new Date(stat.mtime).getTime() : now;
    const age = now - fileTime;

    if (age < CACHE_DURATION) {
      // Cache is valid, return the file as native Capacitor URI
      const fileUri = await Filesystem.getUri({
        path: filePath,
        directory: Directory.Cache,
      });
      return Capacitor.convertFileSrc(fileUri.uri);
    } else {
      // Cache expired, delete it
      try {
        await Filesystem.deleteFile({
          path: filePath,
          directory: Directory.Cache,
        });
      } catch (deleteError) {
        debug.warn('[ImageCache] Failed to delete expired cache file:', deleteError);
      }
    }
  } catch {
    // File doesn't exist or error reading it
    return null;
  }

  return null;
};

/**
 * Download image from web server and cache it
 * Returns native file URI if successful, null otherwise
 */
export const downloadAndCacheImage = async (imageUrl: string): Promise<string | null> => {
  if (!Capacitor.isNativePlatform() || !imageUrl) {
    return null; // No caching needed for web
  }

  // Check if we're online
  if (!navigator.onLine) {
    debug.warn('[ImageCache] Offline, cannot download image:', imageUrl);
    return null;
  }

  try {
    // Determine the full URL
    const isAbsolute = imageUrl.startsWith('http://') || imageUrl.startsWith('https://');
    const baseUrl = getBaseUrl();
    const fullUrl = isAbsolute 
      ? imageUrl 
      : `${baseUrl}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
    
    debug.log('[ImageCache] Downloading missing image:', fullUrl);

    const cacheKey = getCacheKey(imageUrl);
    const filePath = `${CACHE_DIR}/${cacheKey}`;

    // Ensure cache directory exists
    try {
      await Filesystem.mkdir({
        path: CACHE_DIR,
        directory: Directory.Cache,
        recursive: true,
      });
    } catch {
      // Directory might already exist, ignore error
    }

    // Use Capacitor Filesystem's native download routine (bypasses CORS restrictions
    // on some WebViews and avoids huge Base64 memory overhead)
    await Filesystem.downloadFile({
      url: fullUrl,
      path: filePath,
      directory: Directory.Cache,
    });

    debug.log('[ImageCache] Image cached successfully:', cacheKey);

    // Return the natively loadable URI
    const fileUri = await Filesystem.getUri({
      path: filePath,
      directory: Directory.Cache,
    });
    
    return Capacitor.convertFileSrc(fileUri.uri);

  } catch (error) {
    debug.error('[ImageCache] Error downloading image:', error);
    return null;
  }
};

